import { useState, useMemo } from "react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { Loader2, ScrollText, Search, Calendar, Award, Dna } from "lucide-react";
import { useHubPatents, type HubPatent } from "@/hooks/useHubResearch";

function getModalityColor(modality: string | null): string {
  if (!modality) return "bg-gray-100 text-gray-800";
  const m = modality.toLowerCase();
  if (m.includes("cell therapy")) return "bg-green-100 text-green-800";
  if (m.includes("gene therapy")) return "bg-blue-100 text-blue-800";
  if (m.includes("car-t")) return "bg-purple-100 text-purple-800";
  if (m.includes("car-nk")) return "bg-pink-100 text-pink-800";
  if (m.includes("msc")) return "bg-teal-100 text-teal-800";
  if (m.includes("aav")) return "bg-indigo-100 text-indigo-800";
  return "bg-gray-100 text-gray-800";
}

interface PatentsTabProps {
  accountId: string;
  companyName: string;
  hubAccountName: string | null;
}

export function PatentsTab({ accountId, companyName, hubAccountName }: PatentsTabProps) {
  const [search, setSearch] = useState("");
  const [modalityFilter, setModalityFilter] = useState("all");

  const { data: patents, isLoading } = useHubPatents(companyName, hubAccountName);

  const modalities = useMemo(() => {
    if (!patents?.length) return [];
    const mods = new Set(patents.map(p => p.modality).filter(Boolean));
    return Array.from(mods).sort() as string[];
  }, [patents]);

  const filtered = useMemo(() => {
    if (!patents) return [];
    return patents.filter(p => {
      if (modalityFilter !== "all" && p.modality !== modalityFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        return (p.patent_title?.toLowerCase().includes(q) || p.patent_abstract?.toLowerCase().includes(q) || p.assignee_organization?.toLowerCase().includes(q));
      }
      return true;
    });
  }, [patents, search, modalityFilter]);

  const stats = useMemo(() => {
    if (!patents?.length) return { total: 0, avgCitations: 0, modalities: 0, latest: null as string | null };
    const citations = patents.filter(p => p.citation_count != null).map(p => p.citation_count!);
    return {
      total: patents.length,
      avgCitations: citations.length ? Math.round(citations.reduce((a, b) => a + b, 0) / citations.length) : 0,
      modalities: new Set(patents.map(p => p.modality).filter(Boolean)).size,
      latest: patents[0]?.patent_date || null,
    };
  }, [patents]);

  if (isLoading) return <div className="flex items-center justify-center h-[300px]"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  if (!patents?.length) return <div className="p-8 text-center"><ScrollText className="h-12 w-12 mx-auto text-muted-foreground mb-4" /><h3 className="text-lg font-medium mb-2">No Patents Found</h3><p className="text-muted-foreground text-sm">No patents matched for {companyName}</p></div>;

  return (
    <div className="p-6 space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card><CardContent className="p-4"><div className="flex items-center gap-2 text-muted-foreground mb-1"><ScrollText className="h-4 w-4" /><span className="text-xs font-medium">Total Patents</span></div><p className="text-2xl font-bold">{stats.total}</p></CardContent></Card>
        <Card><CardContent className="p-4"><div className="flex items-center gap-2 text-muted-foreground mb-1"><Dna className="h-4 w-4" /><span className="text-xs font-medium">Modalities</span></div><p className="text-2xl font-bold">{stats.modalities}</p></CardContent></Card>
        <Card><CardContent className="p-4"><div className="flex items-center gap-2 text-muted-foreground mb-1"><Award className="h-4 w-4" /><span className="text-xs font-medium">Avg Citations</span></div><p className="text-2xl font-bold">{stats.avgCitations}</p></CardContent></Card>
        <Card><CardContent className="p-4"><div className="flex items-center gap-2 text-muted-foreground mb-1"><Calendar className="h-4 w-4" /><span className="text-xs font-medium">Latest Patent</span></div><p className="text-sm font-bold">{stats.latest ? new Date(stats.latest).toLocaleDateString() : "—"}</p></CardContent></Card>
      </div>

      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search patents..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" />
        </div>
        <Select value={modalityFilter} onValueChange={setModalityFilter}>
          <SelectTrigger className="w-[180px]"><SelectValue placeholder="Modality" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Modalities</SelectItem>
            {modalities.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="text-xs text-muted-foreground">{filtered.length} of {patents.length} patents</div>

      <div className="divide-y border rounded-lg">
        {filtered.map(patent => (
          <div key={patent.id} className="p-4 hover:bg-muted/50 transition-colors">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  {patent.patent_number && <Badge variant="outline" className="text-xs font-mono">{patent.patent_number}</Badge>}
                  {patent.modality && <Badge className={`text-xs ${getModalityColor(patent.modality)}`}>{patent.modality}</Badge>}
                  {patent.therapeutic_area && <Badge variant="secondary" className="text-xs">{patent.therapeutic_area}</Badge>}
                </div>
                <p className="text-sm font-medium line-clamp-2">{patent.patent_title || "Untitled Patent"}</p>
                {patent.assignee_organization && <p className="text-xs text-muted-foreground mt-1">{patent.assignee_organization}</p>}
                <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                  {patent.patent_date && <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{new Date(patent.patent_date).toLocaleDateString()}</span>}
                  {patent.citation_count != null && <span className="flex items-center gap-1"><Award className="h-3 w-3" />{patent.citation_count} citations</span>}
                  {patent.claims_count != null && <span>{patent.claims_count} claims</span>}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
